import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateRefundApplicationErrors,
  CreateRefundOptions,
  QueryRefundsResponse,
  Refund,
  RefundCreatedEnvelope,
  RefundQuery,
  RefundUpdatedEnvelope,
  RefundsQueryBuilder,
  createRefund as universalCreateRefund,
  getRefund as universalGetRefund,
  queryRefunds as universalQueryRefunds,
  typedQueryRefunds as universalTypedQueryRefunds,
} from './payments-refunds-v1-refund-refunds.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function createRefund(httpClient: HttpClient): CreateRefundSignature {
  return (
    refund: NonNullablePaths<Refund, `chargeId`, 2>,
    options?: CreateRefundOptions
  ) =>
    universalCreateRefund(
      refund,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateRefundSignature {
  /**
   * Creates a refund.
   *
   * The refunding process starts immediately after a refund entity is created.
   *
   * If you specify an `amount`, you must also specify a `currency_code`. This currency should be the same as the currency of the charge.
   * If `amount` and `currency_code` are not specified, the refund is created for the full charge amount. If the charge was already partially refunded, this refund will fail.
   * @param - Refund to be created.
   * @returns The created refund.
   */
  (
    refund: NonNullablePaths<Refund, `chargeId`, 2>,
    options?: CreateRefundOptions
  ): Promise<
    NonNullablePaths<Refund, `status` | `statusInfo.code`, 3> & {
      __applicationErrorsType?: CreateRefundApplicationErrors;
    }
  >;
}

export function getRefund(httpClient: HttpClient): GetRefundSignature {
  return (refundId: string) =>
    universalGetRefund(
      refundId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRefundSignature {
  /**
   * Retrieves a refund.
   * @param - ID of the refund to retrieve.
   * @returns The requested refund.
   */
  (refundId: string): Promise<
    NonNullablePaths<Refund, `status` | `statusInfo.code`, 3>
  >;
}

export function queryRefunds(httpClient: HttpClient): QueryRefundsSignature {
  return () =>
    universalQueryRefunds(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryRefundsSignature {
  /**
   * Creates a query to retrieve a list of refunds.
   *
   *
   * The `queryRefunds()` method builds a query to retrieve a list of refunds and returns a [RefundsQueryBuilder](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/ascending) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/find) method.
   *
   * You can refine the query by chaining `RefundsQueryBuilder` methods onto the query. `RefundsQueryBuilder` methods enable you to sort, filter, and control the results that `queryRefunds.find()` returns.
   *
   * The query runs with the following `RefundsQueryBuilder` defaults that you can override:
   *
   * - [`skipTo(0)`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/skip-to)
   * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/limit)
   * - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/descending)
   *
   * The methods that are chained to `queryRefunds()` are applied in the order they are called.
   *
   * The table below shows which `RefundsQueryBuilder` methods are supported for `queryRefunds()`. You can only use one filter method for each property. Only the first filter will work if a property is used in more than one filter.
   */
  (): RefundsQueryBuilder;
}

export function typedQueryRefunds(
  httpClient: HttpClient
): TypedQueryRefundsSignature {
  return (query: RefundQuery) =>
    universalTypedQueryRefunds(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryRefundsSignature {
  /** @returns A `RefundsQueryBuilder` object that contains the refined query. */
  (query: RefundQuery): Promise<
    NonNullablePaths<
      QueryRefundsResponse,
      | `refunds`
      | `refunds.${number}.status`
      | `refunds.${number}.statusInfo.code`,
      5
    >
  >;
}

export const onRefundCreated = EventDefinition(
  'wix.payments.refunds.v1.refund_created',
  true,
  (event: RefundCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<RefundCreatedEnvelope>();
export const onRefundUpdated = EventDefinition(
  'wix.payments.refunds.v1.refund_updated',
  true,
  (event: RefundUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<RefundUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreateRefundOptions,
  CreateRefundRequest,
  CreateRefundResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetRefundRequest,
  GetRefundResponse,
  GetRefundabilityRequest,
  GetRefundabilityResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Initiator,
  MessageEnvelope,
  QueryRefundsRequest,
  QueryRefundsResponse,
  Refund,
  RefundCreatedEnvelope,
  RefundDetails,
  RefundQuerySpec,
  RefundUpdatedEnvelope,
  Refundability,
  RefundabilityDetailsOneOf,
  RefundsQueryBuilder,
  RefundsQueryResult,
  RejectionDetails,
  RejectionReason,
  RestoreInfo,
  SortOrder,
  Sorting,
  Status,
  StatusInfo,
  SyncRefundRequest,
  SyncRefundResponse,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  WebhookIdentityType,
  utils,
} from './payments-refunds-v1-refund-refunds.universal.js';
